import { useState } from "react";

import { useAuth } from "../auth/AuthContext";
import { GENERIC_ERROR_SENTENCE, friendlyError } from "../lib/friendlyError";
import { trpc } from "../trpc";

import { formatMoney } from "./event-registrations/shared";

/**
 * Self-serve membership form. Mirrors the paper form (the same details
 * organisers enter on the Members page). Payment is optional: an
 * organiser records it when it arrives.
 *
 * Submits via adminSubmitApplication; on success the AwaitingApproval
 * page refetches adminMe and shows the "we've got your application" copy.
 */

type Gender = "" | "M" | "F";
type MembershipType = "individual" | "family";

interface FamilyMemberDraft {
  name: string;
  gender: Gender;
}

interface ApplyDraft {
  fullName: string;
  gender: Gender;
  address: string;
  email: string;
  contactNo: string;
  membershipType: MembershipType;
  familyMembers: FamilyMemberDraft[];
  amountPaidDollars: string;
  paymentMethod: string;
  notes: string;
}

function emptyDraft(prefillName: string | null, prefillEmail: string | null) {
  return {
    fullName: prefillName ?? "",
    // SAFETY: the empty string is the form's 'not chosen yet' state; submit rejects it before the value reaches the API.
    gender: "" as Gender,
    address: "",
    email: prefillEmail ?? "",
    contactNo: "",
    // SAFETY: "individual" is one of the MembershipType values.
    membershipType: "individual" as MembershipType,
    familyMembers: [],
    amountPaidDollars: "",
    paymentMethod: "",
    notes: "",
  } satisfies ApplyDraft;
}

/** Published one-time membership fee per type, in dollars. Shown on the
 *  form so applicants see what's owed without having to ask. Fees aren't
 *  validated; an organiser records the actual payment. */
const ONE_TIME_FEE = {
  individual: 10,
  family: 30,
} satisfies Record<MembershipType, number>;

/** A family membership covers this many people besides the applicant. */
const MAX_FAMILY_MEMBERS = 3;

/** Shown when the form was refused for a reason we can't name. */
const SEND_FAILED =
  "We couldn't send your application. Check the boxes above and try again.";

export function ApplyForm({ onSubmitted }: { onSubmitted: () => void }) {
  const { user } = useAuth();
  const [draft, setDraft] = useState<ApplyDraft>(() =>
    emptyDraft(user?.fullName ?? null, user?.email ?? null),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const submit = trpc.adminSubmitApplication.useMutation({
    onSuccess: () => {
      onSubmitted();
    },
    onError: (err) => {
      // Network trouble gets its own sentence; anything else (a refused
      // field, a server fault) points back at the form, never raw text.
      const sentence = friendlyError(err);
      setErrorMessage(
        sentence === GENERIC_ERROR_SENTENCE ? SEND_FAILED : sentence,
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const familyMembers =
      draft.membershipType === "family"
        ? draft.familyMembers
            .filter((f) => f.name.trim().length > 0)
            .map((f) => ({
              name: f.name.trim(),
              gender: f.gender || undefined,
            }))
        : undefined;

    const amountCents = (() => {
      const raw = draft.amountPaidDollars.trim();
      if (!raw) {
        return undefined;
      }
      const parsed = parseFloat(raw);
      if (!Number.isFinite(parsed) || parsed < 0) {
        return undefined;
      }
      return Math.round(parsed * 100);
    })();

    submit.mutate({
      fullName: draft.fullName.trim(),
      gender: draft.gender || undefined,
      address: draft.address.trim() || undefined,
      email: draft.email.trim() || undefined,
      contactNo: draft.contactNo.trim() || undefined,
      membershipType: draft.membershipType,
      familyMembers,
      amountPaidCents: amountCents,
      paymentMethod: draft.paymentMethod.trim() || undefined,
      notes: draft.notes.trim() || undefined,
    });
  };

  const addFamily = () =>
    setDraft((d) => ({
      ...d,
      familyMembers: [...d.familyMembers, { name: "", gender: "" }],
    }));
  const removeFamily = (idx: number) =>
    setDraft((d) => ({
      ...d,
      familyMembers: d.familyMembers.filter((_, i) => i !== idx),
    }));
  const setFamily = (idx: number, next: Partial<FamilyMemberDraft>) =>
    setDraft((d) => ({
      ...d,
      familyMembers: d.familyMembers.map((m, i) =>
        i === idx ? { ...m, ...next } : m,
      ),
    }));

  const fee = formatMoney(ONE_TIME_FEE[draft.membershipType] * 100);

  return (
    <form
      onSubmit={handleSubmit}
      className="surface flex flex-col gap-6 p-5 md:p-7"
    >
      <h2 className="text-[18px] font-semibold text-[color:var(--color-ink)]">
        Membership form
      </h2>

      <Field label="Full name">
        <input
          type="text"
          required
          autoComplete="name"
          value={draft.fullName}
          onChange={(e) =>
            setDraft((d) => ({ ...d, fullName: e.target.value }))
          }
          className={inputClass}
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <FieldGroup label="Gender">
          <GenderRadio
            name="gender"
            value={draft.gender}
            onChange={(v) => setDraft((d) => ({ ...d, gender: v }))}
          />
        </FieldGroup>
        <FieldGroup label="Membership type">
          <div className="flex flex-wrap gap-2">
            <TypeChip
              active={draft.membershipType === "individual"}
              label="Individual"
              onClick={() =>
                setDraft((d) => ({ ...d, membershipType: "individual" }))
              }
            />
            <TypeChip
              active={draft.membershipType === "family"}
              label="Family"
              onClick={() =>
                setDraft((d) => ({ ...d, membershipType: "family" }))
              }
            />
          </div>
        </FieldGroup>
      </div>

      <Field label="Email">
        <input
          type="email"
          autoComplete="email"
          value={draft.email}
          onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
          className={inputClass}
        />
        <span className="text-[14px] font-normal text-[color:var(--color-ink-soft)]">
          We&apos;ll email you about your membership here. It starts as the
          email you signed in with.
        </span>
      </Field>

      <Field label="Phone">
        <input
          type="tel"
          autoComplete="tel"
          value={draft.contactNo}
          onChange={(e) =>
            setDraft((d) => ({ ...d, contactNo: e.target.value }))
          }
          className={inputClass}
        />
      </Field>

      <Field label="Address">
        <textarea
          rows={2}
          autoComplete="street-address"
          value={draft.address}
          onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
          className={inputClass}
        />
      </Field>

      {draft.membershipType === "family" ? (
        <FieldGroup
          label={`Family members (up to ${MAX_FAMILY_MEMBERS} more people)`}
        >
          <div className="flex flex-col gap-3">
            {draft.familyMembers.map((m, i) => (
              <div
                key={i}
                className="flex flex-wrap items-center gap-x-4 gap-y-2"
              >
                <input
                  type="text"
                  required
                  value={m.name}
                  onChange={(e) => setFamily(i, { name: e.target.value })}
                  aria-label={`Family member ${i + 1}, name`}
                  placeholder="Name"
                  className={`${inputClass} min-w-[180px] flex-1`}
                />
                <GenderRadio
                  name={`family-gender-${i}`}
                  value={m.gender}
                  onChange={(v) => setFamily(i, { gender: v })}
                />
                <button
                  type="button"
                  onClick={() => removeFamily(i)}
                  aria-label={`Remove ${m.name.trim() || `family member ${i + 1}`}`}
                  className="inline-flex min-h-11 cursor-pointer items-center px-2 text-[14px] text-[color:var(--color-ink-soft)] underline underline-offset-4 hover:text-[color:var(--color-ink)]"
                >
                  Remove
                </button>
              </div>
            ))}
            {draft.familyMembers.length < MAX_FAMILY_MEMBERS ? (
              <button
                type="button"
                onClick={addFamily}
                className="inline-flex min-h-11 cursor-pointer items-center self-start rounded-full border border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] px-4 text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
              >
                Add a family member
              </button>
            ) : (
              <p className="text-[14px] text-[color:var(--color-ink-soft)]">
                A family membership covers up to {MAX_FAMILY_MEMBERS} more
                people.
              </p>
            )}
          </div>
        </FieldGroup>
      ) : null}

      <div className="border-t border-[color:var(--color-line)] pt-6">
        <h3 className="text-[18px] font-semibold text-[color:var(--color-ink)]">
          Payment
        </h3>
        <p className="mt-2 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
          {draft.membershipType === "family"
            ? `Family membership: ${fee}, paid once.`
            : `Individual membership: ${fee}, paid once.`}{" "}
          Paying later? Leave these boxes blank and an organiser will record
          your payment when it arrives.
        </p>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field label="Amount paid ($)">
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={draft.amountPaidDollars}
              onChange={(e) =>
                setDraft((d) => ({ ...d, amountPaidDollars: e.target.value }))
              }
              className={inputClass}
            />
          </Field>
          <Field label="How you paid">
            <input
              type="text"
              value={draft.paymentMethod}
              onChange={(e) =>
                setDraft((d) => ({ ...d, paymentMethod: e.target.value }))
              }
              placeholder="For example: bank transfer or cash"
              className={inputClass}
            />
          </Field>
        </div>
      </div>

      <Field label="Anything else we should know?">
        <textarea
          rows={3}
          value={draft.notes}
          onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
          placeholder="Optional. Anything you'd like us to know."
          className={inputClass}
        />
      </Field>

      <div className="flex flex-wrap items-center gap-4 border-t border-[color:var(--color-line)] pt-5">
        <button
          type="submit"
          disabled={submit.isPending}
          className="inline-flex min-h-11 cursor-pointer items-center rounded-full bg-[color:var(--color-ink)] px-6 text-[15px] font-semibold text-[color:var(--color-paper)] transition-colors hover:bg-[color:var(--color-amber)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submit.isPending ? "Sending…" : "Send application"}
        </button>
        {errorMessage ? (
          <span
            role="alert"
            className="text-[14px] text-[color:var(--color-danger)]"
          >
            {errorMessage}
          </span>
        ) : (
          <span className="text-[14px] text-[color:var(--color-ink-soft)]">
            We&apos;ll look at it and email you.
          </span>
        )}
      </div>
    </form>
  );
}

// ─── Field primitives ────────────────────────────────────────────────────

const inputClass =
  "min-h-11 w-full rounded-lg border border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] px-3.5 py-2.5 text-[16px] font-normal text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-faint)] outline-none focus:border-[color:var(--color-amber)]";

/** A label wrapping one input, so tapping the words focuses the field. */
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field-label">
      <span>{label}</span>
      {children}
    </label>
  );
}

/** A named group of choices. A plain label would pass a tap on its words
 *  to the first option inside it (picking Male, or resetting Family). */
function FieldGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-1.5">
      <legend className="mb-1.5 text-[14px] font-semibold text-[color:var(--color-ink)]">
        {label}
      </legend>
      {children}
    </fieldset>
  );
}

function GenderRadio({
  name,
  value,
  onChange,
}: {
  /** Groups the two options so arrow keys move between them. */
  name: string;
  value: Gender;
  onChange: (v: Gender) => void;
}) {
  return (
    <div className="flex gap-4">
      {(["M", "F"] as const).map((g) => (
        <label
          key={g}
          className="flex min-h-11 cursor-pointer items-center gap-2 text-[16px] text-[color:var(--color-ink)]"
        >
          <input
            type="radio"
            name={name}
            checked={value === g}
            onChange={() => onChange(g)}
            className="h-5 w-5 accent-[color:var(--color-amber)]"
          />
          {g === "M" ? "Male" : "Female"}
        </label>
      ))}
    </div>
  );
}

function TypeChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex min-h-11 cursor-pointer items-center rounded-full border px-5 text-[15px] font-semibold transition-colors ${
        active
          ? "border-[color:var(--color-amber)] bg-[color:var(--color-amber)]/14 text-[color:var(--color-ink)]"
          : "border-[color:var(--color-line-strong)] bg-[color:var(--color-bg)] text-[color:var(--color-ink-soft)] hover:border-[color:var(--color-ink)]"
      }`}
    >
      {label}
    </button>
  );
}
